import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { Alert, Badge, Button, Card, CardBody, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import InboundJourneyApiV2 from 'api/v2/admins/inbound-journey-api-v2'
import { getPhase3ErrorMessage } from '../helpers/phase3-inbound-journey-helper'

const STATE_OPTIONS = [
  { value: '', label: 'Semua Status' },
  { value: 'created', label: 'Dibuat' },
  { value: 'handover_to_studio', label: 'Diserahkan ke Studio' },
  { value: 'studio_received', label: 'Diterima Studio' },
  { value: 'completed', label: 'Selesai' },
  { value: 'cancelled', label: 'Dibatalkan' }
]

const humanizeState = (value = '') => STATE_OPTIONS.find((option) => option.value === value)?.label || value.toString().split('_').map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')

const stateColor = (state) => {
  if (state === 'handover_to_studio') return 'info'
  if (state === 'completed') return 'success'
  if (state === 'cancelled') return 'danger'
  return 'secondary'
}

const productLinks = (batch = {}) => {
  const products = batch.products || []
  if (!products.length) return '-'

  return products.map((product, index) => {
    const inventoryId = product.inventory?.id

    return (
      <div key={ product.id || index }>
        { inventoryId ? (
          <a href={ `/app/warehouse-team/inventory/${inventoryId}` }>
            { product.sku || product.name || product.id }
          </a>
        ) : (product.sku || product.name || product.id) }
      </div>
    )
  })
}

const Phase3InventoryBatchPage = ({ pageUtils, apiNamespace: propApiNamespace = '' }) => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const apiNamespace = propApiNamespace || queryParams.get('api_namespace') || ''
  const selectedBatchId = queryParams.get('batch_id') || ''
  const [ batches, setBatches ] = useState([])
  const [ selectedBatch, setSelectedBatch ] = useState(null)
  const [ meta, setMeta ] = useState({ current_page: 1, total_pages: 1 })
  const [ q, setQ ] = useState(() => queryParams.get('q') || '')
  const [ state, setState ] = useState(queryParams.get('state') || '')
  const [ handoverNotes, setHandoverNotes ] = useState({})
  const [ isLoading, setIsLoading ] = useState(false)
  const [ isSubmitting, setIsSubmitting ] = useState(false)
  const [ cancelOpen, setCancelOpen ] = useState(false)
  const [ cancelNote, setCancelNote ] = useState('')
  const [ physicalAbsent, setPhysicalAbsent ] = useState(false)
  const [ cancellingBatch, setCancellingBatch ] = useState(false)
  const [ cancelError, setCancelError ] = useState('')

  useEffect(() => {
    fetchBatches(1)
  }, [ state ]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCancelOpen(false)
    setPhysicalAbsent(false)
    if (!selectedBatchId) {
      setSelectedBatch(null)
      return
    }

    InboundJourneyApiV2.showInventoryBatch({ id: selectedBatchId, apiNamespace })
      .then((response) => setSelectedBatch(response.data?.data || response.data || null))
      .catch((error) => {
        const message = getPhase3ErrorMessage(error)
        pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
      })
  }, [ selectedBatchId, apiNamespace ]) // eslint-disable-line react-hooks/exhaustive-deps

  const batchListPath = apiNamespace === 'all_team'
    ? '/app/all-team/inbound-journey/inventory-batches'
    : '/app/inbound-journey/inventory-batches'

  const batchDetailPath = (batch) => {
    const params = new URLSearchParams()
    params.set('batch_id', batch.id)
    if (apiNamespace) params.set('api_namespace', apiNamespace)

    return `${batchListPath}?${params.toString()}`
  }

  const fetchBatches = async (page = 1) => {
    setIsLoading(true)
    try {
      const response = await InboundJourneyApiV2.getInventoryBatches({ q, state, page, per_page: 20, apiNamespace })
      setBatches(response.data?.data || [])
      setMeta(response.data?.meta || { current_page: page, total_pages: 1 })
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handoverToStudio = async (batch) => {
    setIsSubmitting(true)
    try {
      const response = await InboundJourneyApiV2.handoverInventoryBatch({
        id            : batch.id,
        handover_note : handoverNotes[batch.id] || '',
        apiNamespace
      })
      pageUtils?.setAlertMsg?.(response.data?.message || 'Inventory Batch berhasil diserahkan ke Studio Team')
      await fetchBatches(meta.current_page || 1)
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const cancelBatch = async () => {
    if (cancellingBatch || !selectedBatch?.cancel_consignment?.allowed || !cancelNote.trim() || !physicalAbsent) return
    setCancellingBatch(true)
    setCancelError('')
    try {
      const response = await InboundJourneyApiV2.cancelInventoryBatch({
        id: selectedBatch.id, note: cancelNote, physicalAbsent,
        expectedProductIds: selectedBatch.cancel_consignment.product_ids, apiNamespace
      })
      setSelectedBatch(response.data.data)
      setCancelOpen(false)
      pageUtils?.setAlertMsg?.(response.data.message)
      await fetchBatches(meta.current_page || 1)
    } catch (error) {
      setCancelError(getPhase3ErrorMessage(error))
      setPhysicalAbsent(false)
      const latest = await InboundJourneyApiV2.showInventoryBatch({ id: selectedBatch.id, apiNamespace }).catch(() => null)
      if (latest) setSelectedBatch(latest.data?.data || latest.data)
    } finally {
      setCancellingBatch(false)
    }
  }

  const canHandover = !apiNamespace || apiNamespace === 'inbound'
  const canBulkGoLive = !apiNamespace || apiNamespace === 'inbound'

  const bulkGoLiveBatch = async (batch) => {
    if (!batch?.consignment_id) return

    setIsSubmitting(true)
    try {
      const response = await InboundJourneyApiV2.goLiveConsignment({
        id           : batch.consignment_id,
        apiNamespace : apiNamespace || 'inbound'
      })
      pageUtils?.setAlertMsg?.(response.data?.message || 'Produk yang sudah siap pada consignment ini sekarang sudah live')
      await fetchBatches(meta.current_page || 1)
    } catch (error) {
      const message = getPhase3ErrorMessage(error)
      pageUtils?.setApiErrorMsg ? pageUtils.setApiErrorMsg(error.response?.data || message) : alert(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <TitlePage mainTitle='Inbound Journey' subTitle='Inventory Batch' />

      <Card className='mb-3'>
        <CardBody>
          <Row form className='align-items-end'>
            <Col md='5'>
              <FormGroup>
                <Label>Cari Batch / Consignment / SKU</Label>
                <Input value={ q } placeholder='IB-190626..., BKR..., product name' onChange={ (event) => setQ(event.target.value) } />
              </FormGroup>
            </Col>
            <Col md='3'>
              <FormGroup>
                <Label>Status</Label>
                <Input type='select' value={ state } onChange={ (event) => setState(event.target.value) }>
                  { STATE_OPTIONS.map((option) => <option key={ option.value } value={ option.value }>{ option.label }</option>) }
                </Input>
              </FormGroup>
            </Col>
            <Col md='4'>
              <Button color='primary' disabled={ isLoading } onClick={ () => fetchBatches(1) }>
                { isLoading ? 'Memuat...' : 'Muat Ulang' }
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      { selectedBatch && (
        <Card className='mb-3'>
          <CardBody>
            <div className='d-flex justify-content-between align-items-start mb-3'>
              <div>
                <h4 className='mb-1'>Detail Batch: { selectedBatch.batch_code || '-' }</h4>
                { selectedBatch.cancel_consignment && (
                  <div className='my-2'>
                    <Button color='danger' size='sm' disabled={ !selectedBatch.cancel_consignment.allowed || cancellingBatch } onClick={ () => { setCancelError(''); setCancelNote(''); setPhysicalAbsent(false); setCancelOpen(true) } }>Cancel Batch Consignment</Button>
                    { selectedBatch.cancel_consignment.blockers?.length > 0 && (
                      <div className='text-danger mt-2'><strong>Pembatalan batch diblokir:</strong><ul>{ selectedBatch.cancel_consignment.blockers.map((reason) => <li key={ reason }>{ reason }</li>) }</ul></div>
                    ) }
                  </div>
                ) }
                <div className='text-muted'>Consignment #{ selectedBatch.consignment_id || '-' }</div>
              </div>
              <Button color='secondary' outline size='sm' tag='a' href={ batchListPath }>Tutup Detail</Button>
            </div>
            <Row>
              <Col md='3'><strong>Status</strong><div>{ humanizeState(selectedBatch.state) || '-' }</div></Col>
              <Col md='3'><strong>Consignor</strong><div>{ selectedBatch.consignor_detail?.name || selectedBatch.consignor?.name || '-' }</div></Col>
              <Col md='3'><strong>Jumlah</strong><div>{ selectedBatch.quantity_total || '-' }</div></Col>
              <Col md='3'><strong>Handover</strong><div>{ selectedBatch.handover_to || '-' }</div></Col>
            </Row>
            <div className='mt-3'>
              <strong>Produk</strong>
              <div>{ productLinks(selectedBatch) }</div>
            </div>
          </CardBody>
        </Card>
      ) }

      <Modal isOpen={ cancelOpen } toggle={ () => { if (!cancellingBatch) setCancelOpen(false) } } size='lg'>
        <ModalHeader>Cancel Batch Consignment { selectedBatch?.batch_code }</ModalHeader>
        <ModalBody>
          <Alert color='warning'>Seluruh produk dalam batch akan di-soft-delete dan IQ/SQ menjadi 0. Jika satu produk memiliki blocker, tidak ada produk yang dibatalkan. Gunakan Consignor Return bila barang fisik harus dikembalikan.</Alert>
          <ul>{ selectedBatch?.cancel_consignment?.products?.map((product) => <li key={ product.product_id }>{ product.sku }</li>) }</ul>
          { cancelError && <Alert color='danger' style={ { whiteSpace: 'pre-line' } }>{ cancelError }</Alert> }
          <FormGroup><Label>Alasan pembatalan dan verifikator fisik *</Label><Input type='textarea' value={ cancelNote } disabled={ cancellingBatch } onChange={ (event) => setCancelNote(event.target.value) } /></FormGroup>
          <FormGroup check><Label check><Input type='checkbox' checked={ physicalAbsent } disabled={ cancellingBatch } onChange={ (event) => setPhysicalAbsent(event.target.checked) } />Saya memastikan seluruh barang fisik pada batch ini tidak ada.</Label></FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' disabled={ cancellingBatch } onClick={ () => setCancelOpen(false) }>Tutup</Button>
          <Button color='danger' disabled={ cancellingBatch || !cancelNote.trim() || !physicalAbsent || !selectedBatch?.cancel_consignment?.allowed } onClick={ cancelBatch }>{ cancellingBatch ? 'Memproses...' : 'Konfirmasi Cancel Batch Consignment' }</Button>
        </ModalFooter>
      </Modal>

      <Card>
        <CardBody>
          <div className='mb-3 text-muted'>
            <div><strong>Inventory Batch adalah data operasional setelah Create Consign.</strong> Satu batch dapat berisi beberapa produk dari consignment yang sama.</div>
            <div>Gunakan baris batch untuk serah-terima fisik produk ke Inbound / Studio Team. Setelah final check, Inbound dapat melakukan bulk-live untuk produk yang sudah siap dari consignment yang sama.</div>
          </div>

          <Table responsive hover>
            <thead>
              <tr>
                <th>Batch Code</th>
                <th>Status</th>
                <th>Consignor</th>
                <th>Products</th>
                <th>Qty</th>
                <th>Handover</th>
              </tr>
            </thead>
            <tbody>
              { batches.map((batch) => (
                <tr key={ batch.id }>
                  <td>
                    <div><a className='font-weight-bold' href={ batchDetailPath(batch) }>{ batch.batch_code }</a></div>
                    <small className='text-muted'>Consignment #{ batch.consignment_id }</small>
                  </td>
                  <td><Badge color={ stateColor(batch.state) }>{ humanizeState(batch.state) }</Badge></td>
                  <td>
                    <div>{ batch.consignor_detail?.name || batch.consignor?.name || '-' }</div>
                    <small className='text-muted'>{ batch.consignor?.email || '' }</small>
                  </td>
                  <td>
                    <div>{ batch.products_count || 0 } produk</div>
                    <div className='text-muted small'>{ productLinks(batch) }</div>
                  </td>
                  <td>{ batch.quantity_total || '-' }</td>
                </tr>
              )) }
              { !batches.length && !isLoading && (
                <tr>
                  <td colSpan='6' className='text-center text-muted'>Inventory Batch tidak ditemukan.</td>
                </tr>
              ) }
            </tbody>
          </Table>

          <div className='d-flex justify-content-between'>
            <Button disabled={ meta.current_page <= 1 || isLoading } onClick={ () => fetchBatches((meta.current_page || 1) - 1) }>
              Sebelumnya
            </Button>
            <div className='text-muted'>Halaman { meta.current_page || 1 } / { meta.total_pages || 1 }</div>
            <Button disabled={ meta.current_page >= meta.total_pages || isLoading } onClick={ () => fetchBatches((meta.current_page || 1) + 1) }>
              Berikutnya
            </Button>
          </div>
        </CardBody>
      </Card>
    </>
  )
}

export default Phase3InventoryBatchPage
